import { workspaceApi } from "@/api/workspace-api";
import { getErrorMessage } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WorkspaceTypeOptions } from "@/lib/utils";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import posthog from "posthog-js";

interface CreateWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateWorkspaceDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateWorkspaceDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [newWorkspace, setNewWorkspace] = useState({
    opportunity_name: "",
    affiliated_company: "",
    opportunity_type: "",
  });
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateWorkspace = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const response = await workspaceApi.createWorkspace(newWorkspace);
      if (response.status === "success") {
        const newWorkspaceData = response.result;
        await useWorkspaceStore.getState().fetchWorkspaces();

        useWorkspaceStore.getState().setActiveWorkspaceId(newWorkspaceData._id);

        // Track workspace creation event
        posthog.capture("workspace_created", {
          workspace_id: newWorkspaceData._id,
          workspace_name: newWorkspaceData.opportunity_name,
          workspace_type: newWorkspace.opportunity_type,
          $groups: { workspace: newWorkspaceData._id },
        });

        await queryClient.invalidateQueries();

        onOpenChange(false);
        setNewWorkspace({
          opportunity_name: "",
          affiliated_company: "",
          opportunity_type: "",
        });

        toast.success("Workspace created successfully!");

        if (onSuccess) {
          onSuccess();
        } else {
          navigate(`/${newWorkspaceData._id}/dashboard`);
        }
      } else {
        toast.error(response.message || "Failed to create workspace");
      }
    } catch (error) {
      console.error("Failed to create workspace:", error);
      toast.error(
        getErrorMessage(error, "Failed to create workspace. Please try again.")
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Workspace</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Workspace Name</Label>
            <Input
              id="name"
              value={newWorkspace.opportunity_name}
              onChange={(e) =>
                setNewWorkspace({
                  ...newWorkspace,
                  opportunity_name: e.target.value,
                })
              }
              placeholder="Enter workspace name"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="type">Workspace Type</Label>
            <Select
              value={newWorkspace.opportunity_type}
              onValueChange={(value) =>
                setNewWorkspace({ ...newWorkspace, opportunity_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {WorkspaceTypeOptions.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={handleCreateWorkspace}
          disabled={
            isCreating ||
            !newWorkspace.opportunity_name.trim() ||
            !newWorkspace.opportunity_type
          }
        >
          {isCreating ? "Creating..." : "Create Workspace"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}